import React from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import noResultsSrc from 'images/people_no_results.svg';
import emptyGroupSrc from 'images/empty_frame.svg';
import calculatingSrc from 'images/ollie_calculator.svg';
import errorSrc from 'images/error.svg';
import doesNotManageSrc from 'images/llama_outlines.svg';
import * as actions from 'actions/shared/index/data';
import { hasFeatureFlags } from 'utils/features';
import { routes } from '../../constants/config';
import IndexDataView from '../shared/index/data_view';
import ActionButton from '../shared/ActionButton';
import CalculateInformation from './advanced/CalculateInformation';
import { canEditGroups, hasEditSmartGroupsPermissions } from '../../selectors';
import { peopleTableHeaders } from '../../constants/tableHeaders';

const noResults = props => {
  const isSmartGroup =
    props.activeGroupType && props.activeGroupType === 'Group::Smart';
  const isCustomGroup =
    props.activeGroupType && props.activeGroupType === 'Group';
  const hasUnificationFeatureFlag = hasFeatureFlags([
    'readiness-enable-unification',
  ]);

  if (isSmartGroup && !props.hasActiveFilters) {
    return {
      image: emptyGroupSrc,
      title: t('smart_groups.blank_slates.empty_group.title'),
      description: t('smart_groups.blank_slates.empty_group.description'),
      additionalDescription: !hasUnificationFeatureFlag && (
        <CalculateInformation
          groupId={props.activeGroupId}
          calculating={props.activeGroupCalculating}
          refreshData={props.refreshData}
          setGroupInMeta={props.setGroupInMeta}
        />
      ),
    };
  }
  if (isCustomGroup && !props.hasActiveFilters) {
    return {
      image: emptyGroupSrc,
      title: t('groups.blank_slates.is_empty.title'),
      description: t('groups.blank_slates.is_empty.description'),
      additionalDescription: props.canAddPeople &&
        !hasUnificationFeatureFlag && (
          <ActionButton
            prefix="Add Learners"
            label="to This Group"
            href={routes.GROUPS + '/' + props.activeGroupId + '/edit'}
            customButtonStyles="NewBlankSlate-button"
          />
        ),
    };
  }

  return {
    title: 'Bueller? Bueller?',
    image: noResultsSrc,
    additionalDescription: (
      <span>
        Perhaps you’re looking for someone in the{' '}
        <a className="target" href={routes.ARCHIVES()}>
          archive
        </a>
        ?
      </span>
    ),
  };
};

const isCalculating = () => ({
  title: t('smart_groups.blank_slates.calculating.title'),
  image: calculatingSrc,
  description: t('smart_groups.blank_slates.calculating.description'),
});

const invalidSmartGroup = props => {
  const description = t('smart_groups.blank_slates.invalid.description');
  const description2 = t('smart_groups.blank_slates.invalid.description_2');
  const longerDescription = description + ' ' + description2;

  return {
    title: t('smart_groups.blank_slates.invalid.title'),
    image: errorSrc,
    description: props.canEditSmartGroups ? longerDescription : description,
    additionalDescription: props.canEditSmartGroups && (
      <ActionButton
        prefix="Edit"
        label="Rules"
        handleClick={e => props.editRuleBasedGroup(props.activeGroupId)}
        iconName="edit"
        customIconStyles="c-brand-gray-dark"
        customButtonStyles="NewBlankSlate-button"
      />
    ),
  };
};

const noContent = props => {
  const newPersonButtonClasses = classNames('NewBlankSlate-button mr-8', {
    'NewButton--disabled c-p h-td-n h-ctc': !props.hasManagedGroups,
  });

  return {
    image: doesNotManageSrc,
    title: t('groups.blank_slates.do_not_manage_people'),
    description: props.canAddPeople && (
      <div>
        <ActionButton
          label="Add New Person"
          href={routes.NEW_USER}
          customButtonStyles={newPersonButtonClasses}
          customIconStyles="c-brand-gray-dark"
          disabled={!props.hasManagedGroups}
          data-toggle={!props.hasManagedGroups ? 'tooltip' : null}
          data-original-title={
            !props.hasManagedGroups ? t('groups.no_managed_group') : null
          }
        />
        <ActionButton
          label="Add New Group"
          href={routes.NEW_GROUP}
          customButtonStyles="NewBlankSlate-button mr-8"
          customIconStyles="c-brand-gray-dark"
        />
      </div>
    ),
  };
};

const PeopleDataView = props => {
  const blankSlates = {
    hasFailed: { icon: `${routes.ASSETS_BLANK_SLATES}/users_danger.svg` },
    noResults: noResults(props),
    noContent: noContent(props),
    isCalculating: isCalculating(),
    invalidSmartGroup: invalidSmartGroup(props),
  };

  return (
    <IndexDataView
      {...props}
      blankSlates={blankSlates}
      tableHeaders={peopleTableHeaders}
    />
  );
};

const mapStateToProps = state => ({
  activeGroupCalculating: state.filter.activeGroup
    ? state.data.meta.groups[state.filter.activeGroup].calculating
    : null,
  activeGroupId: state.filter.activeGroup,
  activeGroupType: state.filter.activeGroup
    ? state.data.meta.groups[state.filter.activeGroup].type
    : null,
  invalidSmartGroup: state.filter.activeGroup
    ? state.data.meta.groups[state.filter.activeGroup].recalculationState ===
      'invalid'
    : null,
  canAddPeople: canEditGroups(state),
  canEditSmartGroups: hasEditSmartGroupsPermissions(state),
  hasActiveFilters: state.filter.isActive,
});

const mapDispatchToProps = dispatch => ({
  refreshData() {
    dispatch(actions.refreshData());
  },
  setGroupInMeta(data) {
    dispatch(actions.setGroupInMeta(data));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(PeopleDataView);
